import type { ComponentProps, ReactNode } from "react";
import { useRef, useState } from "react";

import { cn } from "../lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

interface TruncateTooltipProps {
  /** Content rendered inside the truncating span. */
  children: ReactNode;
  /**
   * Content shown in the tooltip when the text is clipped. Defaults to
   * `children`, so formatted cell values are mirrored in the tooltip.
   */
  content?: ReactNode;
  /** Extra classes for the truncating span. */
  className?: string;
  /** Positioning of the tooltip relative to the trigger. */
  side?: ComponentProps<typeof TooltipContent>["side"];
}

/**
 * Renders its children inside a truncating span and only opens a tooltip when
 * the text is actually clipped. The overflow check runs lazily on hover (no
 * `ResizeObserver`), which keeps it cheap for virtualized tables with many
 * cells.
 */
export const TruncateTooltip = ({
  children,
  content,
  className,
  side = "top",
}: TruncateTooltipProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <Tooltip
      open={open}
      onOpenChange={(next) => {
        // Suppress opening when the text fits within its container.
        if (
          next &&
          ref.current &&
          ref.current.scrollWidth <= ref.current.clientWidth
        ) {
          return;
        }
        setOpen(next);
      }}
    >
      <TooltipTrigger
        render={
          <span ref={ref} className={cn("block min-w-0 truncate", className)}>
            {children}
          </span>
        }
      />
      <TooltipContent
        side={side}
        className="wrap-break-word whitespace-pre-wrap"
      >
        {content ?? children}
      </TooltipContent>
    </Tooltip>
  );
};
